import Price from "components/common/Price";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { useWishlistContext } from "lib/contexts/WishlistProvider";
import { handleWishlistRemoveItem } from "lib/utils/wishlist-v2";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useLocale } from "lib/hooks/useLocale";
import { WishlistItemType } from "lib/types/wishlist";
import { analyticsEvents, eventTypes, FALLBACK_PRODUCT_IMAGE } from "lib/utils/constants";
import { useSearchParams } from "react-router-dom";
import {
  floorToTwoDecimals,
  insertBeforeLastOccurrence,
  publishPostMessage,
  truncateString,
} from "lib/utils/helpers";
import React, { useMemo } from "react";
import { X, Plus, Heart } from "@phosphor-icons/react";
import { successToast } from "lib/utils/toasters";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { Icon } from "@shopflo/ui/icon";
import { UpsellConfigType } from "lib/types/merchantConfigTypes";
import { getStyleFromCSSVariable, getTextColorByBrightness } from "@shopflo/ui/utils";

interface WishListItemCardProps {
  index: number;
  item: WishlistItemType;
  parent: "UPSELL_STRIP" | "STORE_PAGE";
  customisation?: UpsellConfigType;
}

const WishListItemCard: React.FC<WishListItemCardProps> = ({ index, item, parent, customisation }) => {
  const { t } = useLocale();
  const {
    state: { user },
  } = useUserContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    actions: { refreshWishlist },
  } = useWishlistContext();
  const [searchParams] = useSearchParams();

  const customerId = user?.uid ?? "";

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { handleAddItem } = useItemEdit();

  const wishlistIconFillStyle = getStyleFromCSSVariable('--flo-primary-button-color', 'fill')

  const handleRemoveFromWishlist = async () => {
    const paramsMerchantId = searchParams.get("mid")?.toLowerCase();
    const merchantId: string =
      paramsMerchantId && Boolean(paramsMerchantId)
        ? paramsMerchantId
        : merchant?.merchantId && Boolean(merchant?.merchantId)
          ? merchant?.merchantId
          : "";
    try {
      const success = await handleWishlistRemoveItem(
        merchantId,
        customerId,
        { productId: item?.productId, variantId: item?.variantId },
        // This card renders both inside the cart drawer and on the standalone
        // wishlist page, which are different surfaces to the storefront.
        parent === "UPSELL_STRIP" ? "CART" : "STORE_PAGE",
      );
      if (success) {
        successToast(t("removed_from_wishlist"), 3000);
        sendAnalyticsEvent({
          eventName: analyticsEvents.REMOVED_FROM_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              variantId: item?.variantId,
              variantName: item?.variantName,
              productId: item?.productId,
              productName: item?.productName,
              parent: parent === "UPSELL_STRIP" ? "WISHLIST_STRIP" : "STORE_PAGE",
            },
          },
        });
      }
    } finally {
      refreshWishlist();
    }
  };

  const addItemToCart = () => {
    if (parent === "UPSELL_STRIP") {
      handleAddItem([{ variantId: item?.variantId ?? "", quantity: 1 }]);
    } else {
      publishPostMessage(eventTypes.ADD_TO_CART, { item: { id: item?.variantId, quantity: 1 } });
    }
    sendAnalyticsEvent({
      eventName: analyticsEvents.ADDED_FROM_WISHLIST,
      eventType: "flo_action",
      metaData: {
        wishlistData: {
          variantId: item?.variantId,
          variantName: item?.variantName,
          productId: item?.productId,
          productName: item?.productName,
          parent: parent === "UPSELL_STRIP" ? "WISHLIST_STRIP" : "STORE_PAGE",
        },
      },
    });
  };

  if (parent === "UPSELL_STRIP") {
    return (
      <div
        key={index}
        className="relative flex h-fit w-[196px] snap-start flex-col gap-2 cursor-pointer shrink-0 after:absolute after:top-0 after:bottom-0 after:right-[-15px] after:w-[1px] after:bg-carbon-lighter after:opacity-40 after:content-[''] last:after:hidden">
        <div
          className="flex flex-row gap-2 cursor-pointer"
          onClick={() => publishPostMessage(eventTypes.OPEN_WISHLIST_PRODUCT, { url: item?.link })}>
          <div className="h-[68px] w-[68px] border-1 rounded-lg overflow-hidden">
            <img
              src={insertBeforeLastOccurrence(item?.image, ".jpg", "_small")}
              alt="Cart Item"
              className={`rounded-lg object-cover h-full w-full`}
              draggable={false}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_PRODUCT_IMAGE;
              }}
            />
          </div>
          <div className="flex flex-col flex-1">
            <p className="text-wrap text-text-xs-medium text-coal-dark line-clamp-2"
              style={{
                color: customisation?.textColor,
              }}
            >{item?.productName}</p>
            <p className="text-wrap text-text-xs-regular text-coal-dark line-clamp-1"
              style={{
                color: customisation?.textColor,
              }}
            >{item?.variantName}</p>
            <Price
              total={item?.currentPrice}
              compareAt={item?.originalPrice}
              orientation="vertical"
              customCSS="!flex-row !items-center !space-x-1 !text-sm"
              customization={{
                textColor: customisation?.textColor,
              }}
            />
          </div>
        </div>
        <div className="flex flex-row w-full gap-2">
          <div
            className="w-8 h-8 border-1 rounded-md border border-zinc-200 flex justify-center items-center"
            onClick={handleRemoveFromWishlist}
            style={{
              borderColor: getTextColorByBrightness({ bgHexColor: customisation?.backgroundColor ?? '#FFFFFF', lightOpacity: 24 }),
            }}
          >
            <Icon
              IconComponent={Heart}
              size="md"
              iconProps={{
                className: `h-4 w-4 fill-primary-dark`,
                strokeWidth: 2.5,
                weight: "fill",
                color: getTextColorByBrightness({
                  bgHexColor: customisation?.backgroundColor ?? '#FFFFFF',
                  lightOpacity: 60,
                  darkOpacity: 60
                }),
                style: wishlistIconFillStyle,
              }}
            />
          </div>
          <button
            onClick={addItemToCart}
            className="flex flex-1 items-center justify-center space-x-1 rounded-lg border-1 border-yay-dark p-2 text-sm font-medium text-yay-dark"
            style={{
              borderColor: customisation?.buttonColor,
              color: customisation?.buttonColor,
            }}
          >
            <Plus size={16} />
            <span>{t("add").toUpperCase()}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <li
      key={index}
      className={`relative flex flex-col snap-start gap-3 max-h-[11rem] min-w-[19rem] md:max-w-[19rem] p-3 overflow-hidden rounded-xl border border-carbon-lighter`}>
      <div
        className="flex flex-row space-x-3 cursor-pointer h-[68px] w-[68px]"
        onClick={() => publishPostMessage(eventTypes.OPEN_WISHLIST_PRODUCT, { url: item?.link })}>
        <img
          src={insertBeforeLastOccurrence(item?.image, ".jpg", "_small")}
          alt="Cart Item"
          className={`h-full w-full rounded-lg border border-gray-light object-cover`}
          draggable={false}
        />
        <div className="flex flex-col space-y-2">
          <p className="mt-1 w-[8rem] text-wrap text-sm font-medium text-coal-dark">
            {truncateString(item?.productName, 44)}
          </p>
          <div className="flex flex-row space-x-0.5 text-xs font-medium text-coal-light">
            <span>{item?.variantName}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="pr-6">
          <Price
            total={item?.currentPrice}
            compareAt={item?.originalPrice}
            orientation="vertical"
            customCSS="!flex-row !items-center !space-x-1 !text-sm"
            {...(item?.originalPrice > item?.currentPrice
              ? {
                discountPercentage: floorToTwoDecimals(
                  ((item?.originalPrice - item?.currentPrice) / item?.originalPrice) * 100,
                ),
              }
              : {})}
          />
        </div>
        <button
          onClick={addItemToCart}
          className="flex items-center justify-center space-x-1 rounded-lg border-2 border-yay-dark p-2 text-sm font-medium text-yay-dark">
          <Plus size={16} />
          <span>{t("add").toUpperCase()}</span>
        </button>
      </div>
      <div className="flex flex-row justify-between">
        <button onClick={handleRemoveFromWishlist} className="absolute top-0 right-0 p-2 text-coal-light">
          <X className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
};

export default WishListItemCard;
